import { useState, useCallback } from "react";
import { debounce } from "lodash";

type PaginatedFetchFn<T> = (
  page: number,
  pageSize: number,
  filter: string
) => Promise<{ data: { success: boolean; data: { items: T[]; count: number } } }>;

export const usePaginatedData = <T>(
  fetchFn: PaginatedFetchFn<T>,
  deps: React.DependencyList = []
) => {
  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getDataWithPagination = useCallback(async (page: number, pageSize: number, filter: string) => {
    try {
      setLoading(true);
      const response = await fetchFn(page, pageSize, filter);
      if (!response.data.success) {
        setData([]);
        setTotalCount(0);
        return;
      }
      setData(response.data.data.items ?? []);
      setTotalCount(response.data.data.count ?? 0);
    } catch {
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  const debouncedSearch = useCallback(
    debounce((page: number, rowPerPage: number, searchText: string) => {
      getDataWithPagination(page, rowPerPage, searchText);
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getDataWithPagination, ...deps]
  );

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(0);
  };

  return {
    data,
    totalCount,
    page,
    rowPerPage,
    loading,
    searchText,
    setPage,
    setSearchText,
    getDataWithPagination,
    debouncedSearch,
    handleChangePage,
    handleRowsPerPageChange,
    handleSearchChange,
  };
};
